import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PromoCampaign,
  getCampaigns,
  createCampaign,
  deleteCampaign,
} from '../../services/promoCampaignAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const STATUS_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: '#f3f4f6', color: '#6b7280' },
  ACTIVE: { bg: '#d1fae5', color: '#065f46' },
  PAUSED: { bg: '#fef3c7', color: '#92400e' },
  COMPLETED: { bg: '#dbeafe', color: '#1e40af' },
};

const STATUS_TABS = ['All', 'Active', 'Draft', 'Completed', 'Paused'] as const;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (value: string | null): string => {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return currencyFormatter.format(num);
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

interface CreateFormData {
  name: string;
  campaign_type: string;
  start_date: string;
  end_date: string;
  target_animal_type: string;
  budget: string;
  description: string;
  notes: string;
}

const EMPTY_FORM: CreateFormData = {
  name: '',
  campaign_type: 'FREE_FEED',
  start_date: '',
  end_date: '',
  target_animal_type: '',
  budget: '',
  description: '',
  notes: '',
};

const PromoCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<PromoCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<string>('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<CreateFormData>({ ...EMPTY_FORM });
  const [creating, setCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCampaigns();
      setCampaigns(data);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      setError('Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { All: campaigns.length };
    for (const c of campaigns) {
      const s = c.status;
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    if (activeTab === 'All') return campaigns;
    const statusKey = activeTab.toUpperCase();
    return campaigns.filter((c) => c.status === statusKey);
  }, [campaigns, activeTab]);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.start_date) return;

    setCreating(true);
    try {
      const payload: Partial<PromoCampaign> = {
        name: formData.name.trim(),
        campaign_type: formData.campaign_type,
        start_date: formData.start_date,
      };
      if (formData.end_date) payload.end_date = formData.end_date;
      if (formData.target_animal_type) payload.target_animal_type = formData.target_animal_type;
      if (formData.budget) payload.budget = formData.budget;
      if (formData.description.trim()) payload.description = formData.description.trim();
      if (formData.notes.trim()) payload.notes = formData.notes.trim();

      await createCampaign(payload);
      setShowCreateModal(false);
      setFormData({ ...EMPTY_FORM });
      await loadCampaigns();
    } catch (err) {
      console.error('Failed to create campaign:', err);
      setError('Failed to create campaign.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCampaign(id);
      setDeleteConfirmId(null);
      await loadCampaigns();
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      setError('Failed to delete campaign.');
    }
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <h1>Promo Campaigns</h1>
        <div className="crm-header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + New Campaign
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {STATUS_TABS.map((tab) => {
          const countKey = tab === 'All' ? 'All' : tab.toUpperCase();
          const count = statusCounts[countKey] || 0;
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: isActive ? '#3b82f6' : '#fff',
                color: isActive ? '#fff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {tab} ({count})
            </button>
          );
        })}
      </div>

      {/* Campaign table */}
      {loading ? (
        <div className="loading">Loading campaigns...</div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="empty-state">
          <h3>No campaigns found</h3>
          <p>
            {activeTab === 'All'
              ? 'Create your first promo campaign to get started.'
              : `No ${activeTab.toLowerCase()} campaigns.`}
          </p>
        </div>
      ) : (
        <div className="leads-table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Enrolled</th>
                <th>Total Invested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.map((campaign) => {
                const badge = STATUS_BADGE_COLORS[campaign.status] || STATUS_BADGE_COLORS.DRAFT;
                return (
                  <tr
                    key={campaign.id}
                    onClick={() => navigate(`/crm/promo-campaigns/${campaign.id}`)}
                  >
                    <td>
                      <strong>{campaign.name}</strong>
                    </td>
                    <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                      {campaign.campaign_type_display}
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: badge.bg,
                          color: badge.color,
                          textTransform: 'uppercase',
                        }}
                      >
                        {campaign.status_display || campaign.status}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                      {formatDate(campaign.start_date)}
                    </td>
                    <td style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                      {formatDate(campaign.end_date)}
                    </td>
                    <td style={{ fontSize: '0.9rem' }}>{campaign.enrolled_count}</td>
                    <td style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {formatCurrency(campaign.total_invested)}
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(campaign.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
        Showing {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div
            className="modal"
            style={{ maxWidth: '600px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>New Promo Campaign</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="modal-body">
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>
                    Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Campaign name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Campaign Type</label>
                    <select
                      name="campaign_type"
                      value={formData.campaign_type}
                      onChange={handleFormChange}
                    >
                      <option value="FREE_FEED">Free Feed</option>
                      <option value="DISCOUNT">Discount</option>
                      <option value="TRIAL">Trial</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Target Animal Type</label>
                    <select
                      name="target_animal_type"
                      value={formData.target_animal_type}
                      onChange={handleFormChange}
                    >
                      <option value="">-- None --</option>
                      <option value="POULTRY">Poultry</option>
                      <option value="SWINE">Swine</option>
                      <option value="HORSE">Horse</option>
                      <option value="RUMINANTS">Ruminants</option>
                      <option value="SPECIAL">Special</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Start Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Budget</label>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleFormChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Campaign description..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    placeholder="Internal notes..."
                    rows={2}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId !== null && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="modal" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="modal-close" onClick={() => setDeleteConfirmId(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this campaign? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDelete(deleteConfirmId)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCampaigns;
